import { NgModule } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';

import { WorkOrderRoutingModule } from './work-order-routing.module';
import { WorkOrderOverviewComponent } from './overview/work-order-overview.component';
import { WorkOrderComponent } from './work-order.component';
import { WorkOrderMaterialsComponent } from './materials/work-order-materials.component';
import { WorkOrderDocumentsComponent } from './documents/work-order-documents.component';
import { WorkOrderNavigationComponent } from './work-order-navigation.component';
import { TranslateModule } from '@ngx-translate/core';
import { DocumentPreviewComponent } from './document-preview/document-preview.component';
import { WorkOrderCommentsComponent } from './comments/work-order-comments.component';
import { AddCommentComponent } from './comments/add-comment.component';
import { AttachmentTagComponent } from './comments/attachment-tag.component';
import { ObjectHierarchyComponent } from './objects/object-hierarchy.component';
import { ObjectComponent } from './objects/object/object.component';
import { CommentPanelComponent } from './comment-panel.component';
import { ObjectDocumentsComponent } from './work-lines/object-documents.component';
import { ObjectDocumentsListComponent } from './work-lines/object-documents-list.component';
import { WorkOrderDocumentComponent } from './documents/work-order-document.component';
import { SectionHeaderComponent } from './section-header.component';
import { CommentComponent } from './comments/comment/comment.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ReportProgressFooterComponent } from './objects/report-progress-footer/report-progress-footer.component';
import { LimitProgressDialogComponent } from '@app/work-order/objects/report-progress-footer/limit-progress-dialog/limit-progress-dialog.component';
import { StatusSelectorDialogComponent } from '@app/work-order/objects/report-progress-footer/status-selector-dialog/status-selector-dialog.component';
import { StatusSelectorOptionComponent } from '@app/work-order/objects/report-progress-footer/status-selector-dialog/status-selector-option/status-selector-option.component';
import { SidenavModule } from '@app/sidenav-container/sidenav-container.module';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTabsModule } from '@angular/material/tabs';
import { DialogComponent } from '@app/shared/dialog/dialog.component';
import { MatGridListModule } from '@angular/material/grid-list';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { ProgressIndicatorModule } from '@app/shared/progress-indicator/progress-indicator.module';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SecuredResourceModule } from '@app/shared/secured-resource/secured-resource.module';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { PipesModule } from '@app/shared/pipes/pipes.module';
import { CheckButtonModule } from '@app/shared/check-button/check-button.module';
import { QuantityDialogModule } from '@app/shared/quantity-dialog/quantity-dialog.module';
import { CableDialogModule } from '@app/shared/cable-dialog/cable-dialog.module';
import { CameraModule } from '@app/shared/camera/camera.module';
import { EmptyListModule } from '@app/shared/empty-list/empty-list.module';
import { PutOnHoldInfoDialogModule } from '@app/shared/put-on-hold-info-dialog/put-on-hold-info-dialog.module';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { LongPressDirective } from '@app/shared/directives/long-press.directive';
import { ProgressModule } from '@app/shared/progress/progress.module';
import { MatCardModule } from '@angular/material/card';
import { MIPSCommentsComponent } from '@app/work-order/mips-comments/mips-comments.component';
import { WorkLineModule } from '@app/shared/work-line/work-line.module';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { Model3dViewerComponent } from '@app/work-order/documents/model-3d-viewer/model-3d-viewer.component';
import { VideoViewerComponent } from '@app/work-order/documents/video-viewer/video-viewer.component';
import { PdfMarkupComponent } from './pdf-markup/pdf-markup.component';
import { PdfCanvasComponent } from './pdf-markup/pdf-canvas/pdf-canvas.component';
import { PdfToolbarComponent } from './pdf-markup/pdf-toolbar/pdf-toolbar.component';
import { PdfRedlineComponent } from './pdf-markup/pdf-redline/pdf-redline.component';
import { RedlineToolbarComponent } from './pdf-markup/pdf-redline/redline-toolbar/redline-toolbar.component';
import { WeightReportComponent } from './weight-report/weight-report.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { WorkOrderFeedbackComponent } from './feedback/work-order-feedback.component';
import { NoFeedbackRecipientConfirmDialogComponent } from './feedback/no-feedback-recipient-confirm-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { DocumentStatusDialogComponent } from '@app/work-order/documents/document-status-dialog/document-status-dialog.component';
import { WorkOrderSiteQueriesComponent } from './site-query/work-order-site-queries.component';
import { CreateWorkOrderSiteQueryComponent } from './site-query/create-work-order-site-query.component';

@NgModule({
  declarations: [
    WorkOrderNavigationComponent,
    WorkOrderComponent,
    MIPSCommentsComponent,
    WorkOrderOverviewComponent,
    WorkOrderMaterialsComponent,
    WorkOrderDocumentsComponent,
    WorkOrderDocumentComponent,
    DocumentPreviewComponent,
    WorkOrderCommentsComponent,
    AddCommentComponent,
    AttachmentTagComponent,
    ObjectHierarchyComponent,
    ObjectComponent,
    CommentPanelComponent,
    ObjectDocumentsComponent,
    ObjectDocumentsListComponent,
    SectionHeaderComponent,
    CommentComponent,
    ReportProgressFooterComponent,
    LimitProgressDialogComponent,
    StatusSelectorDialogComponent,
    StatusSelectorOptionComponent,
    DialogComponent,
    LongPressDirective,
    PdfMarkupComponent,
    PdfCanvasComponent,
    PdfToolbarComponent,
    PdfRedlineComponent,
    RedlineToolbarComponent,
    WeightReportComponent,
    WorkOrderFeedbackComponent,
    NoFeedbackRecipientConfirmDialogComponent,
    DocumentStatusDialogComponent,
    WorkOrderSiteQueriesComponent,
    CreateWorkOrderSiteQueryComponent,
  ],
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    NgxSliderModule,
    ReactiveFormsModule,
    MatExpansionModule,
    MatTabsModule,
    MatGridListModule,
    MatCheckboxModule,
    MatProgressBarModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSnackBarModule,
    MatDialogModule,
    MatMenuModule,
    MatRadioModule,
    WorkOrderRoutingModule,
    PipesModule,
    CheckButtonModule,
    QuantityDialogModule,
    CableDialogModule,
    CameraModule,
    SidenavModule,
    SecuredResourceModule,
    ProgressIndicatorModule,
    EmptyListModule,
    PutOnHoldInfoDialogModule,
    ProgressModule,
    MatCardModule,
    WorkLineModule,
    MatProgressSpinnerModule,
    NgxExtendedPdfViewerModule,
    NgOptimizedImage,
    Model3dViewerComponent,
    VideoViewerComponent,
  ],
})
export class WorkOrderModule {}
